import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Wallet, ArrowLeftRight, AlertTriangle, LayoutGrid, Radar, Search,
  Network, Globe2, Cpu, Coins, Settings as SettingsIcon, LogOut, KeyRound, Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/nexus";
import { useAuth } from "@/context/AuthContext";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import brandIcon from "@/assets/tentacles-icon.png";

const NAV = [
  { to: "/", label: "Command", icon: Radar, testid: "nav-command" },
  { to: "/graph", label: "Graph Workspace", icon: Network, testid: "nav-graph" },
  { to: "/map", label: "Global Map", icon: Globe2, testid: "nav-map" },
  { to: "/onchain", label: "On-Chain Intel", icon: Coins, testid: "nav-onchain" },
  { to: "/wallets", label: "Agent Wallets", icon: Wallet, testid: "nav-wallets" },
  { to: "/transactions", label: "Transactions", icon: ArrowLeftRight, testid: "nav-transactions" },
  { to: "/alerts", label: "Alerts", icon: AlertTriangle, testid: "nav-alerts" },
  { to: "/stack", label: "Stack Overlay", icon: LayoutGrid, testid: "nav-stack" },
  { to: "/models", label: "Models", icon: Cpu, testid: "nav-models" },
  { to: "/keys", label: "API Keys", icon: KeyRound, testid: "nav-api-keys" },
  { to: "/users", label: "Users", icon: Users, testid: "nav-users", admin: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

function initials(name, email) {
  const src = (name || email || "?").trim();
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

function TopBar() {
  const [now, setNow] = useState(new Date());
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="h-12 border-b border-nexus-border bg-nexus-panel/80 backdrop-blur flex items-center px-4 gap-4 sticky top-0 z-40">
      <div className="flex items-center gap-2.5">
        <img src={brandIcon} alt="Tentacles" className="w-7 h-7 rounded-[7px] shadow-[0_0_12px_rgba(126,73,139,0.5)]" />
        <div className="font-rajdhani font-bold text-lg tracking-[0.28em] uppercase">
          Tentacles
        </div>
        <span className="text-[10px] font-mono text-nexus-text-3 ml-1 border border-nexus-border px-1.5 py-0.5">
          by Octopi AI
        </span>
      </div>

      <div className="flex-1 flex items-center max-w-xl">
        <div className="w-full flex items-center gap-2 border border-nexus-border bg-nexus-bg px-3 py-1.5 focus-within:border-nexus-cyan/60">
          <Search size={14} className="text-nexus-text-3" />
          <input
            data-testid="global-search-input"
            placeholder="TRY GRAPH WORKSPACE FOR NL QUERIES…"
            className="bg-transparent text-xs font-mono uppercase tracking-widest outline-none flex-1 placeholder:text-nexus-text-3"
            readOnly
          />
          <kbd className="text-[10px] font-mono text-nexus-text-3 border border-nexus-border px-1">/graph</kbd>
        </div>
      </div>

      <div className="flex items-center gap-4 text-[11px] font-mono uppercase tracking-widest">
        <div className="flex items-center gap-1.5 text-nexus-cyan">
          <span className="w-1.5 h-1.5 bg-nexus-cyan ping-dot" style={{ color: "#00F0FF" }} />
          <span data-testid="live-status">LIVE FEED</span>
        </div>
        <div className="text-nexus-text-2" data-testid="utc-clock">
          UTC {now.toISOString().slice(11, 19)}
        </div>

        <div className="relative">
          <button
            data-testid="user-menu-button"
            onClick={() => setMenu((m) => !m)}
            className="flex items-center gap-2 border border-nexus-border hover:border-nexus-cyan/60 px-2 py-1 transition-colors"
          >
            <span className="w-5 h-5 bg-nexus-cyan/15 border border-nexus-cyan/50 text-nexus-cyan flex items-center justify-center text-[9px]">
              {initials(user?.name, user?.email)}
            </span>
            <span className="text-nexus-text-2 normal-case tracking-normal max-w-[120px] truncate">{user?.name || user?.email}</span>
          </button>
          {menu && (
            <div className="absolute right-0 mt-1 w-52 border border-nexus-border bg-nexus-panel z-50">
              <div className="px-3 py-2 border-b border-nexus-border">
                <div className="text-nexus-text normal-case tracking-normal text-[12px] truncate">{user?.name}</div>
                <div className="text-nexus-text-3 normal-case tracking-normal text-[11px] truncate">{user?.email}</div>
                <span data-testid="user-role-badge" className={`inline-block mt-1.5 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border ${user?.role === "admin" ? "border-octopi-purple/60 text-octopi-purple" : "border-nexus-cyan/50 text-nexus-cyan"}`}>
                  {user?.role || "read-only"}
                </span>
              </div>
              <button
                data-testid="change-password-button"
                onClick={() => { setMenu(false); setShowChangePw(true); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-nexus-text-2 hover:text-nexus-cyan hover:bg-nexus-panel-2/50 normal-case tracking-normal text-[12px]"
              >
                <KeyRound size={13} /> Change password
              </button>
              <button
                data-testid="logout-button"
                onClick={doLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-nexus-text-2 hover:text-nexus-rose hover:bg-nexus-panel-2/50 normal-case tracking-normal text-[12px]"
              >
                <LogOut size={13} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
      {(showChangePw || user?.must_change_password) && (
        <ChangePasswordModal
          forced={!!user?.must_change_password}
          onClose={() => setShowChangePw(false)}
        />
      )}
    </div>
  );
}

function FeedRow({ label, ok, syncing }) {
  const cls = syncing ? "text-nexus-amber" : ok ? "text-nexus-cyan" : "text-nexus-rose";
  const state = syncing ? "◉ SYNC" : ok ? "◉ ONLINE" : "◌ OFFLINE";
  return (
    <div className="flex justify-between text-nexus-text-2">
      <span>{label}</span>
      <span className={cls} data-testid={`feed-${label.toLowerCase().replace(/\s+/g, "-")}`}>{state}</span>
    </div>
  );
}

function SideNav() {
  const [health, setHealth] = useState(null);
  const { isAdmin } = useAuth();
  useEffect(() => {
    let alive = true;
    api.get("/onchain/health").then((r) => alive && setHealth(r.data)).catch(() => {});
    return () => { alive = false; };
  }, []);
  const items = NAV.filter((n) => !n.admin || isAdmin);
  return (
    <aside className="w-56 border-r border-nexus-border bg-nexus-panel/50 flex flex-col shrink-0">
      <div className="px-3 py-3 border-b border-nexus-border">
        <div className="panel-title"><span className="dot" />Modules</div>
      </div>
      <nav className="flex-1 py-2 overflow-y-auto">
        {items.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-[13px] font-rajdhani tracking-wider uppercase border-l-2 transition-colors ${
                isActive
                  ? "border-nexus-cyan bg-nexus-cyan/5 text-nexus-cyan"
                  : "border-transparent text-nexus-text-2 hover:text-nexus-text hover:bg-nexus-panel-2/50"
              }`
            }
          >
            <Icon size={15} strokeWidth={1.6} />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-nexus-border">
        <div className="panel-title mb-2"><span className="dot" style={{ background: "#FFB000", boxShadow: "0 0 8px #FFB000" }} />Feeds</div>
        <div className="space-y-1.5 text-[11px] font-mono">
          <FeedRow label="Etherscan" ok={health ? health.etherscan : false} />
          <FeedRow label="Blockscout" ok={health ? health.blockscout : false} />
          <FeedRow label="Arkham" ok={false} syncing />
          <FeedRow label="OpenIntel" ok={!!health} />
        </div>
      </div>
    </aside>
  );
}

export default function Shell() {
  const loc = useLocation();
  return (
    <div className="flex flex-col h-screen">
      <TopBar />
      <div className="flex flex-1 overflow-hidden">
        <SideNav />
        <main key={loc.pathname} className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
